import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeService, UIService } from '@services';
import * as Highcharts from 'highcharts';
import { Subject } from 'rxjs';

import { RemoteServiceFeature } from '@vi-iot/client';

import { ChartExtremes, RemoteServiceStore } from '@store';

import { REMOTE_SERVICE_UNITS_ADAPTER_IT } from '../../../../operational-data-common/services/units-adapter/units-adapter.module';
import { UnitsAdapter } from '../../../../operational-data-common/services/units-adapter/units.adapter';
import { hideTooltipDescriptionForMobileChartOptions } from '../../../../shared/common/highchart-options';
import {
  getChartTooltip,
  getFormatterForLocalizedDate,
} from '../../energy-historical-data-modal/energy-history-chart/chart-options';
import { RemoteServiceChartHelper } from './remote-service-chart-helper';

const hourMs = 3600000;
const dayMs = 86400000;
const monthMs = 2678400000;

export const colors = ['#ff9a00', '#de2500', '#00a4ba', '#000000', '#dedede'];

@Component({
  selector: 'app-remote-service-chart',
  templateUrl: './remote-service-chart.component.html',
  styleUrls: ['./remote-service-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteServiceChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  readonly REMOTE_INSPECTION_PREFIX = '+REMOTE_SERVICE';

  @ViewChild('container') container: ElementRef;

  @Input() chartExtremes: ChartExtremes;
  @Input() chartFeatures: RemoteServiceChartHelper.RemoteServiceFeatureViewLayer[];
  @Input() titleKey: string;
  @Input() chartType: 'line' | 'pie';
  @Input() chartTimestamps: { pointStart: number; pointInterval: number };
  @Input() synchroniseExtremes = false;

  ngUnsubscribe$: Subject<void> = new Subject<void>();
  private isFeatureVisibilityAdded = false;

  initChartOptions: Highcharts.Options = {
    chart: {
      zooming: {
        type: 'x',
      },
      height: 550,
    },
    rangeSelector: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      minRange: hourMs,
      maxRange: monthMs,
      ordinal: false,
      events: {
        setExtremes: (e) => {
          this.setNewExtremes(e);
        },
      },
    },
    yAxis: {},
    scrollbar: {
      enabled: false,
    },
    plotOptions: {
      series: {
        animation: false,
        states: {
          hover: {
            enabled: true,
            lineWidth: 3,
          },
        },
        point: {},
        marker: {
          enabled: false,
        },
        visible: true,
        tooltip: {
          valueSuffix: '°C',
        },
        events: {
          legendItemClick: (e) => {
            e.preventDefault();
          },
        },
      },
      column: {
        pointInterval: 24 * 3600 * 1000, // one day
      },
    },
    tooltip: {
      unitsAdapter: this.unitsAdapter,
      followPointer: true,
      shared: true,
      outside: !this.uiService.isMobileView(),
      shape: 'square',
      backgroundColor: '#fff',
      borderColor: '#fff',
      borderRadius: 8,
      borderWidth: 0,
      useHTML: true,
      style: {
        opacity: this.uiService.isMobileView() ? 0.75 : 1,
      },
      positioner: (labelWidth, _labelHeight, point) => this.positionLabels(labelWidth, point),
    } as any,
    navigator: {
      adaptToUpdatedData: false,
      xAxis: {
        visible: true,
      },
      series: [
        {
          data: [],
          name: 'Navigator',
          lineColor: 'transparent',
          className: 'navigator__chart--hide',
        } as Highcharts.NavigatorSeriesOptions,
      ],
    },
    series: [],
  };

  highCharts: typeof Highcharts = Highcharts;
  chart: Highcharts.Chart;
  chartOptions: Highcharts.Options;
  chartSeries: Highcharts.SeriesSplineOptions[];
  chartColors = [];

  constructor(
    private remoteServiceStore: RemoteServiceStore,
    private translateService: TranslateService,
    private uiService: UIService,
    private dateTimeService: DateTimeService,
    @Inject(REMOTE_SERVICE_UNITS_ADAPTER_IT) private unitsAdapter: UnitsAdapter
  ) {}

  @HostListener('window:resize', ['$event']) onResize(): void {
    this.resizeChart();
  }

  ngOnInit(): void {
    this.initDataSeries();
  }

  ngAfterViewInit(): void {
    // setTimeout needed to fix the https://jira.viessmann-climatesolutions.com/browse/VIG-15388
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.synchroniseExtremes) {
      this.calculateChartExtremes();
    }

    if (
      !this.isFeatureVisibilityAdded &&
      JSON.stringify(changes.chartFeatures.previousValue) !==
        JSON.stringify(changes.chartFeatures.currentValue)
    ) {
      this.chartFeatures = this.chartFeatures.map((feature) => ({ ...feature, isVisible: true }));
      this.isFeatureVisibilityAdded = true;
    }
  }

  setNewExtremes(setExtremesEvent: any): void {
    const isNavigator = setExtremesEvent.trigger === 'navigator';
    const isZoom = setExtremesEvent.trigger === 'zoom';

    if (isNavigator || isZoom) {
      this.remoteServiceStore.setNewChartExtremes(setExtremesEvent.min, setExtremesEvent.max);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    this.chartColors = this?.chart?.options?.colors;
    this.initDataSeries();
    this.calculateInitialChartExtremes();
  }

  private calculateChartExtremes(): void {
    if (this.chartExtremes && this.chart) {
      this.chart.xAxis[0].setExtremes(
        this.chartExtremes.startPointMs,
        this.chartExtremes.endPointMs
      );
    }
  }

  private calculateInitialChartExtremes(): void {
    if (this.chartExtremes && this.chart) {
      const isSameDay = this.chartExtremes.endPointMs - this.chartExtremes.startPointMs < dayMs;
      if (!isSameDay) {
        this.chart.xAxis[0].setExtremes(
          this.chartExtremes.startPointMs,
          this.chartExtremes.endPointMs
        );
      }
    }
  }

  getTitleKey(): string {
    const keyUppercase = this.titleKey.toUpperCase();

    return `${this.REMOTE_INSPECTION_PREFIX}.${keyUppercase}`;
  }

  private initDataSeries(): void {
    const translate = this.translateService;
    this.initChartOptions.plotOptions.series.pointStart = this.chartTimestamps.pointStart;
    this.initChartOptions.plotOptions.series.pointInterval = this.chartTimestamps.pointInterval;
    this.initChartOptions.navigator.xAxis = {
      ...this.initChartOptions.navigator.xAxis,
      labels: {
        formatter(): string {
          return getFormatterForLocalizedDate(translate, this.value);
        },
      },
    };
    this.initChartOptions.xAxis = {
      ...this.initChartOptions.xAxis,
      labels: {
        formatter(): string {
          return getFormatterForLocalizedDate(translate, this.value);
        },
      },
    };

    const locale = this.dateTimeService.getLocale();
    const format = this.dateTimeService.getFormat();

    this.initChartOptions.tooltip = {
      ...this.initChartOptions.tooltip,
      formatter: function (): string {
        return getChartTooltip(this, locale, format);
      },
    };

    const unitTransformedChartFeatures = this.getUnitTransformedChartFeatures();
    this.chartSeries = this.getChartSeries(unitTransformedChartFeatures);

    this.chartOptions = {
      ...this.initChartOptions,
      series: this.chartSeries,
      ...hideTooltipDescriptionForMobileChartOptions,
    };
  }

  private getChartSeries(
    chartData: RemoteServiceChartHelper.RemoteServiceFeatureViewLayer[]
  ): Highcharts.SeriesSplineOptions[] {
    let chartSeriesOptions: Highcharts.SeriesSplineOptions[] = [];
    chartData.forEach((item, index) => {
      let seriesOptions: Highcharts.SeriesSplineOptions = {
        id: item.feature,
        data: this.prepareItemData(item.data),
        color: this.chartColors[index],
        connectNulls: true,
        type: 'spline',
        name: this.translateService.instant(
          `${this.REMOTE_INSPECTION_PREFIX}.${item.name.toUpperCase()}`
        ),
        visible: item.isVisible,
        tooltip: {
          valueSuffix: ` ${this.unitsAdapter.getSuffix(item.unit)}`,
        },
      };

      chartSeriesOptions = chartSeriesOptions.concat(seriesOptions);
    });

    return chartSeriesOptions;
  }

  private prepareItemData(itemData: any[]): any[] {
    // VIG-3198 Display single point on chart
    if (itemData.filter((x) => x !== null).length === 1) {
      return itemData.map((item) => {
        if (item !== null) {
          item = { y: item, marker: { enabled: true } };
        }

        return item;
      });
    } else {
      return itemData;
    }
  }

  private resizeChart(): void {
    this.chartOptions.chart.width = this.container.nativeElement.offsetWidth;
    this.chartOptions.tooltip.outside = !this.uiService.isMobileView();
    this.chartOptions.tooltip.style.opacity = this.uiService.isMobileView() ? 0.75 : 1;
    this.chart.update(this.chartOptions, true);
  }

  private positionLabels(
    labelWidth: number,
    point: Highcharts.TooltipPositionerPointObject
  ): { x: number; y: number } {
    if (this.uiService.isMobileView()) {
      return { x: 5, y: 5 };
    }
    const chart = this.chart;
    const y = point.plotY;
    const defaultX = chart.plotLeft + point.plotX;
    const gap = 30;
    let x: number;

    if (labelWidth + gap > chart.plotWidth - point.plotX) {
      x = defaultX - (labelWidth + gap);

      if (labelWidth + gap > point.plotX) {
        x = defaultX - labelWidth / 2;
      }
    } else {
      x = defaultX + gap;
    }

    return { x, y };
  }

  private getUnitTransformedChartFeatures(): RemoteServiceFeature[] {
    const transformed = this.chartFeatures
      .filter((feature) => feature.isVisible)
      .map((entry) => {
        let newUnit = this.unitsAdapter.getUnit(entry.unit);
        const newData = entry.data.map((singleValue) => {
          let newValue = !singleValue
            ? singleValue
            : this.unitsAdapter.getValue(singleValue, entry.unit);

          return newValue;
        });

        return {
          ...entry,
          data: newData,
          unit: newUnit,
        };
      });

    return transformed;
  }

  isTooltipDisabled(element: HTMLElement): boolean {
    return !!element.scrollWidth ? element.scrollWidth <= element.clientWidth : false;
  }

  changeChartVisibility(featureName: string): void {
    const targetSeries = this.chartOptions.series.find((feature) => feature.name === featureName);

    const visibleSeries = this.chartOptions.series.filter((entry) => entry.visible);

    if (targetSeries && visibleSeries.length <= 1 && targetSeries.visible) {
      return;
    }

    this.chartOptions.series = this.chartOptions.series.map((feature) => {
      if (feature.name === featureName) {
        return { ...feature, visible: !feature.visible };
      } else {
        return feature;
      }
    });

    if (this.chart) {
      this.chart.update(this.chartOptions, true);
    }
  }
}
